import { ChangeDetectorRef, Injectable } from '@angular/core';
import { Observable } from '@app/shared/rx/rx-wrapper';
import { IApiListResponse } from '@app/shared/types/common/epics.type';
import { IFilterQuery } from '@app/shared/types/common/filter-query.type';
import {
  IBaseEntityType,
  IDocumentOperationOptions,
} from '@app/shared/types/entities/base.type';
import { TransferListData } from '@app/shared/types/entities/transfer/transfer.type';
import {
  findIndex,
  get,
  has,
  head,
  isEmpty,
  isEqual,
  isFunction,
  unset,
  forEach,
  isNil,
} from 'lodash';
import { BaseService, IServiceConfig } from '../base';
import { IEvent, IEventStatus, ObjectId, ToastType } from '@app/shared/types';
import { SITE_ID } from '@config/global-constants';
import { lazyInject } from '@app/shared/decorators';
import { XprtDataService } from '../xprt-data';
import { I18n } from '@app/i18n/strings-keys';
import { EVENT_STATUS_VALUE } from '@app/containers/select-event/select-event/select-event.enum';
import { RootModalService } from '@app/modules/modal/services/root-modal-service/root-modal.service';
import { ModalCommandActions } from '@app/modules/modal/modal.enums';
import {
  TRANSFER_STATUS_VALUE,
  SITE_SELECT_TYPE,
} from '@app/shared/types/enums/transfer.enum';
import { getApiCommonErrMsg } from '@shared/utils';
import { ModalNotificationService } from '@shared/services/modal-notification';
import { ENTITY_TYPES } from '@app/shared/types/enums/entityTypes.enum';

@Injectable({
  providedIn: 'root',
})
export class TransferDataService extends BaseService {
  @lazyInject(RootModalService) public modalService: RootModalService;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(ModalNotificationService)
  public notificationService: ModalNotificationService;
  @lazyInject(ChangeDetectorRef) protected cd: ChangeDetectorRef;

  public static config: IServiceConfig = {
    ENTITY_TYPE: ENTITY_TYPES.TRANSFER,
    apiEndpoint: 'transfer',
    customEntityRoute: {
      list: null,
    },
  };

  public event: IEvent;
  public fromSiteName: string;
  public toSiteName: string;
  public siteToId: string = this.tokenSessionService.getToken(SITE_ID);

  public fetchList<T = TransferListData>(
    userQuery?: IFilterQuery,
    options: IDocumentOperationOptions = {},
  ): Observable<IApiListResponse<T>> {
    return super.fetchList(userQuery, options);
  }

  public async validateIsCanSubmit(eventId: ObjectId): Promise<string> {
    const validResults = await Promise.all([
      this.validateIsEventOpened(eventId),
    ]);

    const errorMessages = [
      this.i18nService.instant(
        I18n.ItemCountStrings.FinalizeEventNotAvailable,
      ),
    ];

    const invalidIndex = findIndex(validResults, (valid) => valid);

    return get(errorMessages, invalidIndex);
  }

  public async validateIsEventOpened(eventId: ObjectId): Promise<boolean> {
    const event: IEventStatus[] = await this.xprtDataService
      .getEventDetailById(eventId)
      .toPromise();

    return !isEqual(EVENT_STATUS_VALUE.Open, get(head(event), 'status'));
  }

  public async sendTransfer(
    transferId: string,
    onErrorModalClose?: () => void,
  ): Promise<boolean> {
    try {
      await this.updateDocument({ is_send: true }, transferId).toPromise();

      this.notificationService.toast({
        message: this.i18nService.instant(
          I18n.TransferStrings.UpdateTransfersSuccessfully,
          { did: this.i18nService.instant(I18n.TransferStrings.ActionSent) },
        ),
        level: ToastType.SUCCESS,
      });

      return true;
    } catch (err) {
      this.modalService.alert({
        message:
          getApiCommonErrMsg(err) ??
          this.i18nService.instant(I18n.CommonErrors.CommonError),
        onClose: onErrorModalClose,
      });
    }

    return false;
  }

  public async cancelTransfer(
    transferId: string,
    onErrorModalClose?: () => void,
  ): Promise<boolean> {
    try {
      await this.updateDocument(
        { status: TRANSFER_STATUS_VALUE.Canceled },
        transferId,
      ).toPromise();

      this.notificationService.toast({
        message: this.i18nService.instant(
          I18n.TransferStrings.UpdateTransfersSuccessfully,
          {
            did: this.i18nService.instant(I18n.TransferStrings.ActionCanceled),
          },
        ),
        level: ToastType.SUCCESS,
      });

      return true;
    } catch (err) {
      this.modalService.alert({
        message:
          getApiCommonErrMsg(err) ??
          this.i18nService.instant(I18n.CommonErrors.CommonError),
        onClose: onErrorModalClose,
      });
    }

    return false;
  }

  /**
   * Acknowledges a transfer document by its ID.
   *
   * @param transferId The ID of the transfer document to acknowledge.
   * @param showDefaultErrorMsg Whether to show the default error message if the acknowledge operation fails.
   * @returns Promise<boolean> Returns true if acknowledge is successful, false otherwise.
   */
  public async acknowledge(
    transferId: string,
    showDefaultErrorMsg: boolean,
  ): Promise<boolean> {
    try {
      await this.acknowledgeDocument(transferId);
      return true;
    } catch (err) {
      if (showDefaultErrorMsg) {
        this.modalService.alert({
          message:
            getApiCommonErrMsg(err) ??
            this.i18nService.instant(I18n.CommonErrors.CommonError),
        });
      }
    }
    return false;
  }

  public shouldProceedWithAcknowledge(document: IBaseEntityType): boolean {
    return (
      !isEmpty(document?.updated_items) || !isEmpty(document?.unavailable_items)
    );
  }

  public acknowledgeCurrentDocument(currentDocument: IBaseEntityType): void {
    forEach(['updated_items', 'unavailable_items'], (key) => {
      if (has(currentDocument, key)) {
        unset(currentDocument, key);
      }
    });
  }

  /**
   * Handles the acknowledge logic for a transfer document.
   *
   * @param document The transfer document to check and acknowledge if needed.
   * @param showDefaultErrorMsg Whether to show the default error message if acknowledge fails. Default is true.
   * @returns Promise<boolean> Returns true if acknowledge is successful or not needed, false if failed.
   */
  public async handleAcknowledge(
    document: IBaseEntityType,
    showDefaultErrorMsg: boolean = true,
  ): Promise<boolean> {
    const shouldAcknowledge = this.shouldProceedWithAcknowledge(document);
    if (shouldAcknowledge) {
      const acknowledgeResult: boolean = await this.acknowledge(
        document._id,
        showDefaultErrorMsg,
      );
      if (!acknowledgeResult) {
        return false;
      }
    }
    return true;
  }

  public hasChange(
    latestDocument: IBaseEntityType,
    currentDocument: IBaseEntityType,
  ): boolean {
    const updateAtInCurrentDocument = get(currentDocument, 'updated_at');
    const updatedAtInLastedTransfer = get(latestDocument, 'updated_at');

    return !isEqual(updateAtInCurrentDocument, updatedAtInLastedTransfer);
  }

  public async confirm(confirmationText: string): Promise<boolean> {
    const confirmResult = await this.modalService
      .confirm({
        message: confirmationText,
        okText: I18n.CommonButtons.Yes,
        cancelText: I18n.CommonButtons.No,
      })
      .promiseResult();

    return isEqual(ModalCommandActions.APPLY, confirmResult.action);
  }

  public async onAction(options: {
    did: string;
    count?: number;
    onConfirm?: () => Promise<void>;
  }): Promise<void> {
    const confirmed = await this.confirm(
      isNil(options.count)
        ? this.i18nService.instant(I18n.CommonMessages.AreYouSureWantTo, {
            did: options.did,
          })
        : this.i18nService.instant(
            I18n.TransferStrings.ConfirmToUpdateTransfer,
            {
              did: options.did,
              count: options.count,
            },
          ),
    );

    if (confirmed) {
      if (isFunction(options.onConfirm)) {
        await options.onConfirm();
      }
      this.cd.markForCheck();
    }
  }

  public formatSiteName(transfer: IBaseEntityType, siteType: string): string {
    if (!transfer) {
      return '';
    }
    if (SITE_SELECT_TYPE.FROM === siteType) {
      return `${get(transfer, 'site_name')} [${get(transfer, 'store_number')}]`;
    } else {
      return `${get(transfer, 'site_name_to')} [${get(transfer, 'store_number_to')}]`;
    }
  }
}
